import type {
  HokkaidoAirportInspectionCategory,
  HokkaidoAirportInspectionItem,
  HokkaidoAirportInspectionMaster,
  HokkaidoAirportInspectionSymbol,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportInspectionResults,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportInspection';
import type {
  HokkaidoAirportReport,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  InspectionCommentLineLimits,
  InspectionSheet,
  InspectionSheetSourceReport,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportInspectionSheet';

type HokkaidoAirportInspectionResult = HokkaidoAirportInspectionResults[string];

export const getInspectionCarTypes = (
  master: HokkaidoAirportInspectionMaster, airportName: string, carType: string,
): string[] => {
  const pairedCarTypes = master.paired_car_types[airportName]?.[carType];
  return pairedCarTypes ?? [carType];
};

// 空港＋車種に該当する項目だけを残したカテゴリを返す
export const filterInspectionCategories = (
  master: HokkaidoAirportInspectionMaster, airportName: string, carType: string,
): HokkaidoAirportInspectionCategory[] => {
  return master.categories
    .map(category => ({
      ...category,
      items: category.items.filter(item => item.applicable_car_types[airportName]?.includes(carType)),
    }))
    .filter(category => category.items.length > 0);
};

export const isInspectionCompleted = (
  categories: HokkaidoAirportInspectionCategory[], result: HokkaidoAirportInspectionResult | undefined,
): boolean => {
  if (categories.length === 0) return false;
  if (!result) return false;

  return categories.every(category => category.items.every(item => !!result.items[item.key]));
};

// 空港＋車種で点検する全車種（2車両点検は両方）の全項目に記号が入っていれば true
export const isAllInspectionCompleted = (
  master: HokkaidoAirportInspectionMaster,
  airportName: string,
  carType: string,
  results: HokkaidoAirportInspectionResults,
): boolean => {
  return getInspectionCarTypes(master, airportName, carType).every(inspectionCarType => {
    const categories = filterInspectionCategories(master, airportName, inspectionCarType);
    return isInspectionCompleted(categories, results[inspectionCarType]);
  });
};

// 開始前・終了時のどちらかの点検を完了した場合、空港名・車種はログアウトまで変更不可。スキップは点検に数えない。
// サーバーの HokkaidoAirportReport::isVehicleSelectionLocked と同じ条件
export const isVehicleSelectionLocked = (
  report: Pick<HokkaidoAirportReport, 'preWorkInspectionTs' | 'postWorkInspectionTs'>,
): boolean => {
  return !!report.preWorkInspectionTs || !!report.postWorkInspectionTs;
};

export const initInspectionResults = (carTypes: string[]): HokkaidoAirportInspectionResults => {
  return Object.fromEntries(carTypes.map(carType => [carType, { items: {}, comment: '' }]));
};

// 開始前・終了時の両方がスキップなら空配列
export const buildInspectionSheets = (
  report: InspectionSheetSourceReport,
  master: HokkaidoAirportInspectionMaster,
): InspectionSheet[] => {
  const preWorkResults = report.preWorkInspectionResults;
  const postWorkResults = report.postWorkInspectionResults;
  if (!preWorkResults && !postWorkResults) return [];

  const carTypes = getInspectionCarTypes(master, report.airportName, report.carType);
  return carTypes.map(carType => {
    const preWork = preWorkResults?.[carType];
    const postWork = postWorkResults?.[carType];
    const categories = filterInspectionCategories(master, report.airportName, carType);
    const rows = categories.flatMap(category => {
      const hasSubCategory = category.items.some(item => item.sub_category !== null);
      return category.items.map((item, index) => ({
        categoryName: category.name,
        categoryRowSpan: index === 0 ? category.items.length : 0,
        categoryColSpan: hasSubCategory ? 1 : 2,
        subCategory: item.sub_category,
        subCategoryRowSpan: getSubCategoryRowSpan(category.items, index),
        content: item.content,
        preWorkSymbol: preWork?.items[item.key] ?? '',
        postWorkSymbol: postWork?.items[item.key] ?? '',
      }));
    });
    return {
      carType,
      rows,
      preWorkComment: preWork?.comment ?? '',
      postWorkComment: postWork?.comment ?? '',
    };
  });
};

// 1ページに収まらない場合に、そのページの最後の表を次のページの先頭へ移す。表は途中で分けずに丸ごと移す。
// 表が1つだけのページは移せないため、そのまま返す
export const moveLastInspectionSheetToNextPage = (
  pages: InspectionSheet[][], pageIndex: number,
): InspectionSheet[][] => {
  const page = pages[pageIndex];
  if (page.length <= 1) return pages;

  const movedSheet = page[page.length - 1];
  const nextPage = pages[pageIndex + 1] ?? [];
  return [
    ...pages.slice(0, pageIndex),
    page.slice(0, -1),
    [movedSheet, ...nextPage],
    ...pages.slice(pageIndex + 2),
  ];
};

// 印刷の点検表の備考は、開始前・終了時を合わせてこの行数までにする
export const INSPECTION_COMMENT_MAX_LINES = 12;

// 備考の開始前・終了時の行数から、それぞれ何行まで出すかを返す。
// 合計が上限を超える場合は、短い方を上限の半分（切り捨て）まで優先して出し、残りを長い方に回す
export const getInspectionCommentLineLimits = (
  preWorkLines: number, postWorkLines: number,
): InspectionCommentLineLimits => {
  if (preWorkLines + postWorkLines <= INSPECTION_COMMENT_MAX_LINES) {
    return { preWork: preWorkLines, postWork: postWorkLines };
  }

  const shorterLimit = Math.min(preWorkLines, postWorkLines, Math.floor(INSPECTION_COMMENT_MAX_LINES / 2));
  const longerLimit = INSPECTION_COMMENT_MAX_LINES - shorterLimit;
  // 同じ行数なら開始前を長い方として扱う
  if (preWorkLines >= postWorkLines) {
    return { preWork: longerLimit, postWork: shorterLimit };
  }
  return { preWork: shorterLimit, postWork: longerLimit };
};

// フッタ「記号例：良 V　分解点検 W　修理 △　取替 ×　調整 A　締付 T　対象外 ー」
export const formatInspectionLegend = (symbols: HokkaidoAirportInspectionSymbol[]): string => {
  return symbols.map(symbol => `${symbol.name} ${symbol.key}`).join('　');
};

// 下位区分のセルを縦に結合する行数。下位区分が無い行と、同じ下位区分の2行目以降は 0
const getSubCategoryRowSpan = (items: HokkaidoAirportInspectionItem[], index: number): number => {
  const subCategory = items[index].sub_category;
  if (subCategory === null) return 0;
  if (index > 0 && items[index - 1].sub_category === subCategory) return 0;

  const nextGroupIndex = items.findIndex((item, i) => i > index && item.sub_category !== subCategory);
  return (nextGroupIndex === -1 ? items.length : nextGroupIndex) - index;
};
